"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useLayoutEffect, useState } from "react";

const storageKey = "entradas:pending-form-action";
const viewportLockClass = "action-navigation-lock";

type PendingAction = {
  path: string;
  scrollX: number;
  scrollY: number;
  submittedAt: number;
};

type Feedback = {
  message: string;
  danger?: boolean;
};

const successMessages: Record<string, string> = {
  approved: "El evento se actualizó correctamente.",
  cancelled: "La operación se canceló correctamente.",
  channels: "Los canales de venta se guardaron correctamente.",
  collaborator: "El colaborador se guardó correctamente.",
  configured: "La configuración se guardó correctamente.",
  copiedEvent: "El evento se copió correctamente.",
  copiedFunction: "La función se copió correctamente.",
  created: "Los datos se guardaron correctamente.",
  createdFunction: "La función se creó correctamente.",
  dateVisibility: "La visibilidad de la fecha se actualizó correctamente.",
  discount: "El descuento se guardó correctamente.",
  event: "La información del evento se guardó correctamente.",
  inventory: "El inventario se guardó correctamente.",
  layout: "El plano se guardó correctamente.",
  paymentDemo: "La configuración de Mercado Pago se actualizó correctamente.",
  refund: "La devolución se actualizó correctamente.",
  refundMode: "La configuración de devoluciones se guardó correctamente.",
  reassigned: "La entrada se reasignó correctamente.",
  reprogrammed: "La función se guardó correctamente.",
  reviews: "Las invitaciones se procesaron correctamente.",
  sector: "El sector se guardó correctamente.",
  sent: "Tu respuesta se guardó correctamente.",
  ticket: "El ingreso se registró correctamente.",
  url: "La URL de compra se guardó correctamente.",
  visibility: "La visibilidad se actualizó correctamente.",
};

function feedbackFrom(searchParams: URLSearchParams): Feedback {
  const error = searchParams.get("error");
  if (error) return { message: error, danger: true };

  for (const [key, message] of Object.entries(successMessages)) {
    if (searchParams.has(key)) return { message };
  }
  return { message: "Los cambios se guardaron correctamente." };
}

function lockViewport(pending: PendingAction) {
  document.body.style.setProperty(
    "--action-scroll-top",
    `${-pending.scrollY}px`,
  );
  document.body.style.setProperty(
    "--action-scroll-left",
    `${-pending.scrollX}px`,
  );
  document.body.classList.add(viewportLockClass);
}

function unlockViewport() {
  document.body.classList.remove(viewportLockClass);
  document.body.style.removeProperty("--action-scroll-top");
  document.body.style.removeProperty("--action-scroll-left");
}

export function ActionFeedback() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.toString();
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  useEffect(() => {
    function rememberPosition(event: SubmitEvent) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute("method")?.toLowerCase() === "get") return;
      const pending: PendingAction = {
        path: window.location.pathname,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        submittedAt: Date.now(),
      };
      sessionStorage.setItem(storageKey, JSON.stringify(pending));
      lockViewport(pending);
      window.setTimeout(() => {
        const current = sessionStorage.getItem(storageKey);
        if (!current) return;
        try {
          const stored = JSON.parse(current) as PendingAction;
          if (stored.submittedAt !== pending.submittedAt) return;
        } catch {
          // El dato inválido se limpia junto con el bloqueo visual.
        }
        sessionStorage.removeItem(storageKey);
        unlockViewport();
      }, 15_000);
    }

    document.addEventListener("submit", rememberPosition, true);
    return () => document.removeEventListener("submit", rememberPosition, true);
  }, []);

  useLayoutEffect(() => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;

    let pending: PendingAction;
    try {
      pending = JSON.parse(raw) as PendingAction;
    } catch {
      sessionStorage.removeItem(storageKey);
      unlockViewport();
      return;
    }

    if (Date.now() - pending.submittedAt > 60_000) {
      sessionStorage.removeItem(storageKey);
      unlockViewport();
      return;
    }
    if (pending.path !== pathname) {
      sessionStorage.removeItem(storageKey);
      unlockViewport();
      return;
    }
    sessionStorage.removeItem(storageKey);
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    requestAnimationFrame(() => {
      unlockViewport();
      window.scrollTo({
        left: pending.scrollX,
        top: pending.scrollY,
        behavior: "auto",
      });
      root.style.scrollBehavior = previousBehavior;
    });
    setFeedback(feedbackFrom(new URLSearchParams(query)));
  }, [pathname, query]);

  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(null), 5_000);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  if (!feedback) return null;
  return (
    <div
      className={`action-feedback${feedback.danger ? " danger" : ""}`}
      role={feedback.danger ? "alert" : "status"}
      aria-live={feedback.danger ? "assertive" : "polite"}
    >
      <span aria-hidden="true">{feedback.danger ? "!" : "✓"}</span>
      <p>{feedback.message}</p>
      <button
        type="button"
        aria-label="Cerrar confirmación"
        onClick={() => setFeedback(null)}
      >
        ×
      </button>
    </div>
  );
}
